import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Form } from '@amzn/awsui-components-react-v2';

import {
    adminCreateUser,
    selectIsLoading,
} from '../../../store/slices/selectedUserSlice';
import { useNotifications } from '../../../../common/context/grimsbyNotifications';
import useFormValidation, {
    ValidationType,
} from '../../../../common/utils/formValidation';
import { UserProfileData } from '../../../../common/interfaces/userProfile';
import { FormSectionProps } from '../../../../common/interfaces/formSectionProps';
import AdminUserManagementFormSection from '../FormSections/AdminUserManagementFormSection';
import { FormSectionMode } from '../../../../common/constants/forms';
import { UserFormData } from '../../../interfaces/userForm';
import { handleScrollToError } from '../../../../common/utils/scrollToError';

const createUserValidationConfig: {
    [key in ValidationType]?: Array<keyof UserFormData>;
} = {
    required: [
        'programs',
        'regions',
        'email',
        'first_name',
        'last_name',
        'vias_roles',
    ],
    email: ['email'],
};

const AdminUserManagementCreateForm = ({
    createUserFormState,
}: {
    createUserFormState: UserFormData;
}) => {
    const isLoading = useSelector(selectIsLoading);
    const history = useHistory();
    const dispatch = useDispatch();
    const { addNotification } = useNotifications();

    const { isInvalid, errors, validateForm } =
        useFormValidation<UserFormData>();

    const [formValues, setFormValues] =
        useState<UserFormData>(createUserFormState);

    const handleFieldEvent = (changes: Partial<UserFormData>) => {
        setFormValues((values) => ({
            ...values,
            ...changes,
        }));
    };

    const createUserProps: Omit<
        FormSectionProps<UserFormData>,
        'validateAndHandleFieldEvent'
    > & {
        name: string;
    } = {
        formValues,
        errors,
        handleFieldEvent,
        name: 'New User',
        mode: FormSectionMode.Create,
    };

    useEffect(() => {
        if (isInvalid) {
            validateForm(formValues, createUserValidationConfig);
        }
    }, [isInvalid, formValues, validateForm]);

    return (
        <section data-testid="AdminUserManagementCreateForm">
            <Form
                header="Add user"
                actions={
                    <div className="awsui-util-action-stripe awsui-util-mb-m">
                        <div className="awsui-util-action-stripe-group">
                            <Button
                                variant="link"
                                className="admin-user-cancel"
                                data-testid="AdminUserManagementCreateFormCancel"
                                onClick={() => {
                                    history.push({
                                        pathname: `/admin/user-management/users`,
                                    });
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="admin-user-save"
                                data-testid="AdminUserManagementCreateFormAdd"
                                loading={isLoading}
                                onClick={async () => {
                                    const invalid = validateForm(
                                        formValues,
                                        createUserValidationConfig,
                                    );

                                    if (invalid) {
                                        handleScrollToError();
                                    } else {
                                        const {
                                            isSuccessful,
                                            createdUserId,
                                            errorType,
                                            errorMessage,
                                        } = await dispatch<any>(
                                            adminCreateUser(
                                                formValues as Pick<
                                                    UserProfileData,
                                                    | 'programs'
                                                    | 'regions'
                                                    | 'email'
                                                    | 'first_name'
                                                    | 'last_name'
                                                >,
                                            ),
                                        );
                                        addNotification({
                                            id: `create-user-${Date.now()}`,
                                            ...(isSuccessful
                                                ? {
                                                      type: 'success',
                                                      content:
                                                          'You have successfully created a user.',
                                                  }
                                                : {
                                                      type: 'error',
                                                      content: errorMessage,
                                                  }),
                                        });
                                        if (isSuccessful) {
                                            history.push({
                                                pathname: `/admin/user-management/users/${createdUserId}`,
                                            });
                                        } else {
                                            handleScrollToError(
                                                'awsui-app-layout__content',
                                                true,
                                            );
                                        }
                                    }
                                }}
                            >
                                {`${isLoading ? 'Adding' : 'Add'} user`}
                            </Button>
                        </div>
                    </div>
                }
            >
                <AdminUserManagementFormSection {...createUserProps} />
            </Form>
        </section>
    );
};

export default AdminUserManagementCreateForm;
